import axios from 'axios';
import { parseKeywords } from './keywordValidator';

/**
 * Clean and format ISBN by stripping dashes and spaces
 */
export function sanitizeIsbn(isbnStr) {
  if (!isbnStr) return '';
  return String(isbnStr).replace(/[^0-9X]/gi, '').toUpperCase();
}

/**
 * Fetches bibliographic metadata by ISBN using Open Library and Google Books API
 * @param {string} rawIsbn - ISBN-10 or ISBN-13
 * @returns {Promise<{ success: boolean, book?: object, error?: string }>}
 */
export async function fetchBookMetadataByIsbn(rawIsbn) {
  const cleanIsbn = sanitizeIsbn(rawIsbn);
  if (!cleanIsbn || (cleanIsbn.length !== 10 && cleanIsbn.length !== 13)) {
    return {
      success: false,
      error: 'Please enter a valid 10-digit or 13-digit ISBN number.'
    };
  }

  // 1. Try Open Library API
  try {
    const olUrl = `https://openlibrary.org/api/books?bibkeys=ISBN:${cleanIsbn}&format=json&jscmd=data`;
    const olResponse = await axios.get(olUrl, { timeout: 6000 });
    const bookObj = olResponse.data[`ISBN:${cleanIsbn}`];

    if (bookObj && bookObj.title) {
      const authors = (bookObj.authors || []).map((a) => a.name).join(', ');
      const publishers = (bookObj.publishers || []).map((p) => p.name).join(', ');
      const rawSubjects = (bookObj.subjects || []).map((s) => s.name);
      const keywords = parseKeywords(rawSubjects).slice(0, 10).join(', ');

      let publishYear = '';
      if (bookObj.publish_date) {
        const yearMatch = String(bookObj.publish_date).match(/\b(19\d\d|20\d\d)\b/);
        publishYear = yearMatch ? yearMatch[0] : bookObj.publish_date;
      }

      return {
        success: true,
        source: 'Open Library',
        book: {
          bookTitle: (bookObj.title || '').toUpperCase(),
          bookSubTitle: (bookObj.subtitle || '').toUpperCase(),
          bookAuthor: authors ? authors.toUpperCase() : '',
          bookPublisherName: publishers ? publishers.toUpperCase() : '',
          bookPublishYear: publishYear,
          bookPages: bookObj.number_of_pages ? String(bookObj.number_of_pages) : '',
          bookKeywords: keywords ? keywords.toUpperCase() : '',
          bookAbstract: typeof bookObj.notes === 'string' ? bookObj.notes : '',
          isbn: rawIsbn.toUpperCase(),
          coverUrl: bookObj.cover?.large || bookObj.cover?.medium || ''
        }
      };
    }
  } catch (err) {
    console.warn('Open Library lookup failed, trying Google Books fallback...', err.message);
  }

  // 2. Fallback to Google Books API
  try {
    const gbUrl = `https://www.googleapis.com/books/v1/volumes?q=isbn:${cleanIsbn}`;
    const gbResponse = await axios.get(gbUrl, { timeout: 6000 });
    const items = gbResponse.data?.items || [];

    if (items.length > 0 && items[0].volumeInfo) {
      const info = items[0].volumeInfo;
      const authors = (info.authors || []).join(', ');
      const categories = (info.categories || []).join(', ');
      const keywords = parseKeywords(categories).slice(0, 10).join(', ');

      let publishYear = '';
      if (info.publishedDate) {
        const yearMatch = String(info.publishedDate).match(/\b(19\d\d|20\d\d)\b/);
        publishYear = yearMatch ? yearMatch[0] : info.publishedDate;
      }

      return {
        success: true,
        source: 'Google Books',
        book: {
          bookTitle: (info.title || '').toUpperCase(),
          bookSubTitle: (info.subtitle || '').toUpperCase(),
          bookAuthor: authors ? authors.toUpperCase() : '',
          bookPublisherName: (info.publisher || '').toUpperCase(),
          bookPublishYear: publishYear,
          bookPages: info.pageCount ? String(info.pageCount) : '',
          bookKeywords: keywords ? keywords.toUpperCase() : '',
          bookAbstract: info.description || '',
          isbn: rawIsbn.toUpperCase(),
          coverUrl: info.imageLinks?.thumbnail || ''
        }
      };
    }
  } catch (err) {
    console.warn('Google Books lookup failed:', err.message);
  }

  return {
    success: false,
    error: `No bibliographic record found online for ISBN "${rawIsbn}". You can enter the details manually.`
  };
}
